import React, { useState, useEffect, useCallback } from 'react';
import {
    TrendingUp, Search, RefreshCw, ExternalLink, ChevronDown, ChevronUp,
    AlertCircle, CheckCircle, Link2, Lightbulb,
} from 'lucide-react';
import { GlassCard, GlassButton } from '../ui';
import {
    getStrikingDistanceOpportunities, enrichStrikingDistance, applyInternalLinks,
    StrikingDistanceResponse, StrikingDistanceOpportunity, StrikingDistanceEnrichResponse,
    LinkOpportunity, InternalLinkSuggestion,
} from '../../services/api';
import './StrikingDistancePanel.css';

interface StrikingDistancePanelProps {
    projectId: string;
}

/** Show the URL path (more scannable than the full origin). */
const displayPage = (page: string): string => {
    try {
        const u = new URL(page);
        return (u.pathname || '/') + (u.search || '');
    } catch {
        return page;
    }
};

const rowKey = (opp: StrikingDistanceOpportunity) => `${opp.page}__${opp.keyword}`;

type EnrichStatus = 'idle' | 'loading' | 'done' | 'error';
type ApplyStatus = 'idle' | 'applying' | 'done' | 'error';

const StrikingDistancePanel: React.FC<StrikingDistancePanelProps> = ({ projectId }) => {
    const [data, setData] = useState<StrikingDistanceResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Per-row enrichment state
    const [enrichData, setEnrichData] = useState<Record<string, StrikingDistanceEnrichResponse>>({});
    const [enrichStatus, setEnrichStatus] = useState<Record<string, EnrichStatus>>({});
    const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

    // Per-link-suggestion apply state
    const [applyStatus, setApplyStatus] = useState<Record<string, ApplyStatus>>({});

    const fetchOpportunities = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await getStrikingDistanceOpportunities(projectId);
            setData(result);
            setEnrichData({});
            setEnrichStatus({});
            setExpandedRows(new Set());
        } catch (err: any) {
            const detail = err?.response?.data?.detail;
            setError(typeof detail === 'string' ? detail : 'Failed to load striking-distance opportunities.');
        } finally {
            setLoading(false);
        }
    }, [projectId]);

    useEffect(() => {
        fetchOpportunities();
    }, [fetchOpportunities]);

    const handleGetFixes = async (opp: StrikingDistanceOpportunity) => {
        const key = rowKey(opp);
        // Toggle collapse if already enriched
        if (enrichStatus[key] === 'done') {
            setExpandedRows(prev => {
                const next = new Set(prev);
                if (next.has(key)) next.delete(key); else next.add(key);
                return next;
            });
            return;
        }
        setEnrichStatus(prev => ({ ...prev, [key]: 'loading' }));
        setExpandedRows(prev => new Set(prev).add(key));
        try {
            const result = await enrichStrikingDistance(projectId, opp.page, opp.keyword);
            setEnrichData(prev => ({ ...prev, [key]: result }));
            setEnrichStatus(prev => ({ ...prev, [key]: 'done' }));
        } catch (err: any) {
            setEnrichStatus(prev => ({ ...prev, [key]: 'error' }));
        }
    };

    /**
     * Apply a link suggestion from the enrich panel.
     *
     * Role-swap adapter: find_link_opportunities returns source_* fields (the
     * existing article that SHOULD link to the striking-distance page). But
     * applyInternalLinks(taskId, suggestions) applies suggestions TO taskId's
     * HTML and expects target_* fields per suggestion. So we must:
     *   - Edit: the SOURCE article (source_task_id)
     *   - Target in the suggestion payload: the STRIKING page's task_id
     *
     * See spec §Known Risks "LinkOpportunity vs Suggestion shape mismatch on Apply".
     */
    const handleApplyLink = async (
        linkOpp: LinkOpportunity,
        strikingTaskId: string | null,
        linkKey: string,
    ) => {
        if (!strikingTaskId) return;
        setApplyStatus(prev => ({ ...prev, [linkKey]: 'applying' }));
        try {
            // Build an InternalLinkSuggestion with role-swapped source/target.
            // find_link_opportunities returns source_* = the ARTICLE being edited;
            // the striking page is the LINK TARGET (strikingTaskId).
            // target_url/target_title are unused by apply_suggestions (it reads
            // target_task_id + insertion_html); set to '' to avoid misleading data.
            const adapted: InternalLinkSuggestion = {
                target_task_id: strikingTaskId,       // the striking page is the LINK TARGET
                target_url: '',                        // unused by apply_suggestions — striking page URL not available here
                target_title: '',                      // unused by apply_suggestions — striking page title not available here
                anchor_text: linkOpp.anchor_text,
                sentence_context: linkOpp.sentence_context,
                confidence: linkOpp.confidence,
                insertion_html: linkOpp.insertion_html,
            };
            // Apply TO the source article (source_task_id)
            await applyInternalLinks(linkOpp.source_task_id, [adapted], 'manual');
            setApplyStatus(prev => ({ ...prev, [linkKey]: 'done' }));
        } catch {
            setApplyStatus(prev => ({ ...prev, [linkKey]: 'error' }));
        }
    };

    const gscConnected = data?.summary?.gsc_connected ?? false;
    const opportunities = data?.opportunities ?? [];
    const days = data?.summary?.params?.days ?? 90;

    const renderEnrichDetail = (opp: StrikingDistanceOpportunity) => {
        const key = rowKey(opp);
        const status = enrichStatus[key];
        const enriched = enrichData[key];

        if (!expandedRows.has(key)) return null;

        return (
            <tr key={`${key}-detail`} className="sdp-detail-row">
                <td colSpan={6}>
                    <div className="sdp-detail">
                        {status === 'loading' && (
                            <div className="sdp-loading">
                                <RefreshCw size={14} className="sdp-spinning" />
                                <span>Analysing opportunities…</span>
                            </div>
                        )}
                        {status === 'error' && (
                            <div className="sdp-detail-error">
                                <AlertCircle size={14} />
                                <span>Failed to load — try again.</span>
                            </div>
                        )}
                        {status === 'done' && enriched && (
                            <>
                                {enriched.low_confidence && (
                                    <div className="sdp-low-confidence">
                                        <AlertCircle size={13} />
                                        <span>Best-effort suggestions (limited page content available).</span>
                                    </div>
                                )}

                                {enriched.missing_sections.length > 0 && (
                                    <div className="sdp-section">
                                        <div className="sdp-section-title">
                                            <Lightbulb size={14} />
                                            <span>Missing sections to make this page more comprehensive for "{opp.keyword}"</span>
                                        </div>
                                        <ul className="sdp-missing-list">
                                            {enriched.missing_sections.map((s, i) => (
                                                <li key={i}>{s}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {enriched.internal_link_suggestions.length > 0 && (
                                    <div className="sdp-section">
                                        <div className="sdp-section-title">
                                            <Link2 size={14} />
                                            <span>Internal links to add — these existing articles should link to this page</span>
                                        </div>
                                        <div className="sdp-link-suggestions">
                                            {enriched.internal_link_suggestions.map((link, i) => {
                                                const linkKey = `${key}-link-${i}`;
                                                const applyS = applyStatus[linkKey] || 'idle';
                                                return (
                                                    <div key={i} className="sdp-link-row">
                                                        <div className="sdp-link-info">
                                                            <span className="sdp-link-source">
                                                                {link.source_title || displayPage(link.source_url)}
                                                            </span>
                                                            <span className="sdp-link-anchor">
                                                                Anchor: "{link.anchor_text}"
                                                            </span>
                                                            <span className="sdp-link-context sdp-text-secondary">
                                                                {link.sentence_context}
                                                            </span>
                                                        </div>
                                                        <div className="sdp-link-action">
                                                            {applyS === 'done' ? (
                                                                <span className="sdp-apply-done">
                                                                    <CheckCircle size={13} />
                                                                    Applied
                                                                </span>
                                                            ) : (
                                                                <GlassButton
                                                                    size="sm"
                                                                    variant="secondary"
                                                                    disabled={applyS === 'applying'}
                                                                    loading={applyS === 'applying'}
                                                                    onClick={() =>
                                                                        handleApplyLink(link, enriched.task_id, linkKey)
                                                                    }
                                                                >
                                                                    {applyS === 'applying' ? 'Applying…' : 'Apply link'}
                                                                </GlassButton>
                                                            )}
                                                            {applyS === 'error' && (
                                                                <span className="sdp-apply-error">
                                                                    <AlertCircle size={12} /> Failed
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                {enriched.missing_sections.length === 0 &&
                                    enriched.internal_link_suggestions.length === 0 && (
                                        <p className="sdp-text-secondary" style={{ margin: 0 }}>
                                            No specific suggestions generated — try again or add more content to the page.
                                        </p>
                                    )}
                            </>
                        )}
                    </div>
                </td>
            </tr>
        );
    };

    return (
        <GlassCard className="sdp-panel">
            <div className="sdp-header">
                <div className="sdp-title">
                    <TrendingUp size={16} />
                    <span>Striking-Distance Keywords</span>
                </div>
                <div className="sdp-header-right">
                    {data && gscConnected && (
                        <span className="sdp-summary-text">
                            {data.summary.opportunities_found} keywords · positions 11–20
                        </span>
                    )}
                    <GlassButton
                        variant="ghost"
                        size="sm"
                        onClick={fetchOpportunities}
                        disabled={loading}
                        title="Reload opportunities"
                        icon={<RefreshCw size={16} className={loading ? 'sdp-spinning' : ''} />}
                    />
                </div>
            </div>
            <p className="sdp-subtitle">
                Keywords ranking just off page 1 (positions 11–20) — last {days} days. One targeted update can push these to the top.
            </p>

            {loading && (
                <div className="sdp-loading">
                    <RefreshCw size={16} className="sdp-spinning" />
                    <span>Loading opportunities…</span>
                </div>
            )}

            {!loading && error && (
                <div className="sdp-error">
                    <AlertCircle size={16} />
                    <span>{error}</span>
                </div>
            )}

            {!loading && !error && data && !gscConnected && (
                <div className="sdp-empty">
                    <Search size={32} className="sdp-empty-icon" />
                    <p>Connect Google Search Console to surface striking-distance keywords.</p>
                    <p className="sdp-empty-hint">Use the Performance tab to connect your site.</p>
                </div>
            )}

            {!loading && !error && data && gscConnected && opportunities.length === 0 && (
                <div className="sdp-empty">
                    <CheckCircle size={32} className="sdp-empty-icon sdp-empty-icon--ok" />
                    <p>No striking-distance keywords found for the current filters.</p>
                </div>
            )}

            {!loading && !error && opportunities.length > 0 && (
                <table className="seo-keyword-table sdp-table">
                    <thead>
                        <tr>
                            <th>Page</th>
                            <th>Keyword</th>
                            <th style={{ textAlign: 'right' }}>Impressions</th>
                            <th style={{ textAlign: 'right' }}>Position</th>
                            <th style={{ textAlign: 'right' }}>CTR</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {opportunities.map(opp => {
                            const key = rowKey(opp);
                            const status = enrichStatus[key] || 'idle';
                            const expanded = expandedRows.has(key);
                            return (
                                <React.Fragment key={key}>
                                    <tr>
                                        <td>
                                            <div className="sdp-page-cell" title={opp.page}>
                                                {displayPage(opp.page)}
                                            </div>
                                        </td>
                                        <td className="sdp-keyword">{opp.keyword}</td>
                                        <td style={{ textAlign: 'right', fontWeight: 600 }}>
                                            {opp.impressions.toLocaleString()}
                                        </td>
                                        <td style={{ textAlign: 'right', color: 'var(--text-secondary)' }}>
                                            {opp.position != null ? opp.position.toFixed(1) : '—'}
                                        </td>
                                        <td style={{ textAlign: 'right', color: 'var(--text-secondary)' }}>
                                            {(opp.ctr * 100).toFixed(1)}%
                                        </td>
                                        <td className="sdp-action-cell">
                                            <div className="sdp-action">
                                                <GlassButton
                                                    size="sm"
                                                    variant="primary"
                                                    pill
                                                    disabled={status === 'loading'}
                                                    loading={status === 'loading'}
                                                    onClick={() => handleGetFixes(opp)}
                                                    title="Analyse this page for missing sections and internal-link opportunities"
                                                >
                                                    {status !== 'loading' && (expanded
                                                        ? <ChevronUp size={14} />
                                                        : null
                                                    )}
                                                    {status === 'loading' ? 'Analysing…' : 'Get fixes'}
                                                </GlassButton>
                                                <a
                                                    href={opp.page}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="sdp-page-link"
                                                    title="Open page"
                                                >
                                                    <ExternalLink size={13} />
                                                </a>
                                            </div>
                                        </td>
                                    </tr>
                                    {renderEnrichDetail(opp)}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>
            )}
        </GlassCard>
    );
};

export default StrikingDistancePanel;
